import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ExportParameter} from '../../../../../../Model/exportParameter/ExportParameter';
import {SearchForm, SearchFormOptionItem} from '../../../../../../Model/Dto/SearchForm';
import {SearchFormsComponent} from '../../../../../../catalog/catalog/searchForms/search-forms.component';
import {SearchFormsService} from '../../../../../../Services/SearchForms/searchforms.service';
import {SearchFormSearchRequest} from "../../../../../../Model/Dto/SearchFormSearchRequest";
import {LoginService} from "../../../../../../Services/login.service";

interface ExportProductType {
  id: string;
  text: string;
}

@Component({
    selector: 'np-filter-config',
    templateUrl: './filter-config.component.html',
    styleUrls: ['./filter-config.component.css'],
    standalone: false
})
export class FilterConfigComponent implements OnInit {
  @Input() exportParameter: ExportParameter;

  @Input() set popupOpen(value: boolean) {
    const changed = value !== this._popupOpen;
    this._popupOpen = value;
    if (changed && value) {
      this.onPopupOpened();
    }
  }
  get popupOpen(): boolean {
    return this._popupOpen;
  }
  private _popupOpen = false;

  @ViewChild(SearchFormsComponent)
  searchFormsRef: SearchFormsComponent;

  exportProductTypes: ExportProductType[] = [];
  selectedExportProductType: ExportProductType;
  public searchFormPopupVisible: boolean = false;
  public initialSearchForm: SearchForm;
  public refreshWithSearchForm: SearchForm;
  private searchFormSyncedFromService = false;

  @Output() activeSearchFormChange = new EventEmitter<SearchForm>();

  constructor(
    public translate: TranslateService,
    private searchFormsService: SearchFormsService,
    public loginService: LoginService
  ) {}

  private onPopupOpened() {
    const active = this.searchFormsService.activeSearchForm;
    if (active) {
      this.searchFormSyncedFromService = true;
      let catalogId = this.exportParameter.catalogId;
      let customerId = this.exportParameter.customerId;
      let searchFormId = active.id;
      let filterCondition = active.filterCondition;
      let criteria = active.searchOptions.map(criteria => ({
        systemKey: criteria.nextPimField.systemKey,
        elementKey: criteria.nextPimField.elementKey,
        field: criteria.nextPimField.field,
        operator: criteria.operator,
        dataType: criteria.dataType,
        values: criteria.values,
        value: criteria.value,
        valueTo: criteria.valueTo,
      }));

      this.exportParameter.searchFormSearchRequest = {
        catalogId,
        customerId,
        formId: searchFormId,
        condition: filterCondition,
        criteria
      } as SearchFormSearchRequest;
      this.refreshWithSearchForm = { ...active };
      this.activeSearchFormChange.emit(active);
    } else if (!this.searchFormSyncedFromService && this.exportParameter?.searchFormSearchRequest) {
      const req = this.exportParameter.searchFormSearchRequest;
      this.refreshWithSearchForm = {
        id: req.formId,
        filterCondition: req.condition,
        searchOptions: req.criteria?.map(c => ({
          nextPimField: { systemKey: c.systemKey, elementKey: c.elementKey, field: c.field },
          operator: c.operator,
          dataType: c.dataType,
          values: c.values,
          value: c.value,
          valueTo: c.valueTo,
        }) as SearchFormOptionItem)
      } as SearchForm;
      this.activeSearchFormChange.emit(this.refreshWithSearchForm);
    } else {
      this.exportParameter.searchFormSearchRequest = null;
      this.refreshWithSearchForm = null;
      this.activeSearchFormChange.emit(null);
    }
  }

  ngOnInit(): void {
    this.exportProductTypes.push(
      {
        id: '',
        text: this.translate.instant(this.exportParameter.isUpdateFilter ? 'UpdateAllProducts' : 'ExportAllProducts')
      },
      {
        id: 'onlyMasters',
        text: this.translate.instant(
          this.exportParameter.isUpdateFilter ? 'UpdateOnlyMasterProducts' : 'ExportOnlyMasterProducts'
        )
      },
      {
        id: 'onlyVariants',
        text: this.translate.instant(
          this.exportParameter.isUpdateFilter ? 'UpdateOnlyVariantProducts' : 'ExportOnlyVariantProducts'
        )
      },
      {
        id: 'noMasters',
        text: this.translate.instant(this.exportParameter.isUpdateFilter ? 'UpdateAllButMaster' : 'ExportAllButMaster')
      },
      {
        id: 'noVariants',
        text: this.translate.instant(this.exportParameter.isUpdateFilter ? 'UpdateAllButVariants' : 'ExportAllButVariants')
      },
      {
        id: 'onlyVariantsAndMasters',
        text: this.translate.instant(this.exportParameter.isUpdateFilter ? 'UpdateOnlyButVariantsAndMasters' : 'ExportOnlyVariantsAndMasters')
      },
      {
        id: 'onlyStandalone',
        text: this.translate.instant(this.exportParameter.isUpdateFilter ? 'UpdateOnlyStandalone' : 'ExportOnlyStandalone')
      }
    );

    const preselected =
      this.exportProductTypes.find(
        t =>
          t.id && 
          Object.prototype.hasOwnProperty.call(this.exportParameter, t.id) &&
          this.exportParameter[t.id] === true
      ) ?? this.exportProductTypes[0];

    this.selectedExportProductType = preselected;
  }

  isExcel(): boolean {
    return (
      this.exportParameter.version == 'FIELDLIST' ||
      this.exportParameter.format == 'FIELDLIST' ||
      this.exportParameter.version == 'TEMPLATE' ||
      this.exportParameter.format == 'TEMPLATE'
    );
  }

  onExportProductsChanged(event) {
    const { id } = event.value;
    this.selectedExportProductType = this.exportProductTypes[0];
    if (id != '') this.exportParameter.exportMasterOfFilteredChilds = false;

    for (const item of this.exportProductTypes) {
      const active = item.id === id;
      if (active) {
        this.selectedExportProductType = item;
      }
      if (item.id !== '') {
        this.exportParameter[item.id] = active;
      }
    }
  }
  onSearchFormSelected(searchForm: SearchForm) {
    if(searchForm == null) 
      return;
    
    let catalogId = this.exportParameter.catalogId;
    let customerId = this.exportParameter.customerId;
    let searchFormId = searchForm.id;
    let filterCondition = searchForm.filterCondition;
    let criteria = searchForm.searchOptions.map(criteria => ({
      systemKey: criteria.nextPimField.systemKey,
      elementKey: criteria.nextPimField.elementKey,
      field: criteria.nextPimField.field,
      operator: criteria.operator,
      dataType: criteria.dataType,
      values: criteria.values,
      value: criteria.value,
      valueTo: criteria.valueTo
    }));

    this.exportParameter.searchFormSearchRequest = {
      catalogId,
      customerId,
      formId: searchFormId,
      condition: filterCondition,
      criteria
    } as SearchFormSearchRequest;
    
    this.activeSearchFormChange.emit(searchForm ?? null);
  }

  toggleSeachFormPopup(searchForm?: SearchForm) {
    this.initialSearchForm = searchForm ? { ...searchForm } : null;
    this.searchFormPopupVisible = true;
  }

  onSearchFormPopupVisibleChange(visible: boolean) {
    this.searchFormPopupVisible = visible;
  }

  onSearchFormSaved(searchForm: SearchForm) {
    this.refreshWithSearchForm = { ...searchForm };
    this.searchFormPopupVisible = false;
  }

  onSaveSearchForm = () => this.searchFormsRef?.onSave();
  onCancelSearchForm = () => this.searchFormsRef?.onCancel();

}
